import { useEffect, useState } from "react";
import { useOrg } from "@/context/OrgContext";
import { api, getErrorMessage, formatCurrency } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Receipt, Download, Loader2, CheckCircle2, AlertCircle, Clock,
  XCircle, RefreshCw, Printer,
} from "lucide-react";

const STATUS_BADGE = {
  paid: { label: "Payé", icon: CheckCircle2, cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  unpaid: { label: "En attente", icon: Clock, cls: "bg-amber-100 text-amber-800 border-amber-300" },
  expired: { label: "Expiré", icon: XCircle, cls: "bg-gray-100 text-gray-800 border-gray-300" },
  no_payment_required: { label: "Gratuit", icon: CheckCircle2, cls: "bg-gray-100 text-gray-800 border-gray-300" },
};

const SUB_STATUS_BADGE = {
  trialing: { label: "Essai", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  active: { label: "Actif", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  past_due: { label: "Paiement en retard", cls: "bg-orange-100 text-orange-800 border-orange-300" },
  canceled: { label: "Annulé", cls: "bg-red-100 text-red-800 border-red-300" },
  suspended: { label: "Suspendu", cls: "bg-red-100 text-red-800 border-red-300" },
};

const formatDate = (iso) => iso ? new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

export default function BillingPage() {
  const { activeOrg, activeOrgId, isOrgAdmin, refresh } = useOrg();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchHistory = async () => {
    if (!activeOrgId) return;
    setLoading(true);
    try {
      const res = await api.getBillingHistory(activeOrgId);
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Chargement impossible"));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchHistory(); }, [activeOrgId]);

  const handlePrintInvoice = async (tx) => {
    try {
      const res = await api.getInvoiceData(activeOrgId, tx.id);
      const inv = res.data;
      // Ouvre une fenêtre d'impression avec un HTML simple — l'utilisateur peut "Enregistrer en PDF"
      const w = window.open("", "_blank", "width=800,height=900");
      if (!w) {
        toast.error("Bloquez les pop-ups pour imprimer la facture");
        return;
      }
      const t = inv.transaction;
      const o = inv.organization;
      const issuer = inv.issuer;
      w.document.write(`
        <!doctype html><html><head><meta charset="utf-8"><title>Facture ${t.id.slice(0, 8)}</title>
        <style>
          body { font-family: ui-sans-serif, system-ui, sans-serif; padding: 32px; color: #0f172a; }
          h1 { font-size: 28px; margin: 0 0 4px; }
          .header { display: flex; justify-content: space-between; align-items: start; margin-bottom: 32px; padding-bottom: 16px; border-bottom: 2px solid #4f46e5; }
          .meta { font-size: 12px; color: #64748b; }
          h2 { font-size: 14px; text-transform: uppercase; color: #64748b; margin: 24px 0 8px; letter-spacing: 0.05em; }
          table { width: 100%; border-collapse: collapse; margin: 8px 0 24px; }
          th, td { padding: 10px 8px; text-align: left; border-bottom: 1px solid #e2e8f0; }
          th { background: #f8fafc; font-weight: 600; font-size: 12px; text-transform: uppercase; color: #475569; }
          .total { font-size: 22px; font-weight: 700; text-align: right; padding: 12px 8px; background: #eef2ff; color: #4f46e5; }
          .footer { margin-top: 48px; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 12px; }
          @media print { body { padding: 16px; } }
        </style></head><body>
        <div class="header">
          <div>
            <h1>Facture</h1>
            <div class="meta">N° ${t.id.slice(0, 8).toUpperCase()}</div>
            <div class="meta">Émise le ${new Date(t.created_at).toLocaleDateString("fr-FR")}</div>
          </div>
          <div style="text-align: right">
            <strong>${issuer.name}</strong><br>
            <span class="meta">TVA : ${issuer.vat_id || "—"}</span><br>
            <span class="meta">${issuer.address || ""}</span>
          </div>
        </div>
        <h2>Facturé à</h2>
        <strong>${o.name}</strong><br>
        <span class="meta">${o.billing_email || ""}</span><br>
        <span class="meta">Plan : ${o.plan}</span>

        <h2>Détail</h2>
        <table>
          <thead><tr><th>Description</th><th style="text-align:right">Quantité</th><th style="text-align:right">Total HT</th></tr></thead>
          <tbody>
            <tr>
              <td>${t.purpose === "buy_slots" ? "Utilisateurs supplémentaires (15€/utilisateur)" : t.purpose}</td>
              <td style="text-align:right">${t.slots}</td>
              <td style="text-align:right">${(t.amount_eur).toFixed(2)} €</td>
            </tr>
          </tbody>
        </table>
        <div class="total">Total TTC : ${t.amount_eur.toFixed(2)} €</div>

        <h2>Paiement</h2>
        <p class="meta">
          ID Stripe : ${t.session_id}<br>
          Statut : ${t.payment_status}<br>
          ${t.applied_at ? `Appliqué le ${formatDate(t.applied_at)}` : ""}
        </p>

        <div class="footer">
          Bati Assistant — Émis automatiquement. En cas de question, écrivez-nous.<br>
          TVA non applicable en mode test. Ce document fait foi de paiement.
        </div>
        <script>setTimeout(() => window.print(), 250);</script>
        </body></html>
      `);
      w.document.close();
    } catch (err) {
      toast.error(getErrorMessage(err, "Facture non disponible"));
    }
  };

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await api.cancelSubscription(activeOrgId, cancelReason || null);
      toast.success("Abonnement annulé");
      setCancelOpen(false);
      setCancelReason("");
      await refresh();
      await fetchHistory();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResume = async () => {
    if (!window.confirm("Réactiver votre abonnement ?")) return;
    try {
      await api.resumeSubscription(activeOrgId);
      toast.success("Abonnement réactivé");
      await refresh();
      await fetchHistory();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  if (!activeOrg) {
    return <div className="p-8 text-center text-muted-foreground">Chargement…</div>;
  }

  const subStatus = data?.organization?.subscription_status || activeOrg.subscription_status;
  const statusInfo = SUB_STATUS_BADGE[subStatus] || { label: subStatus, cls: "bg-gray-100" };

  return (
    <div className="space-y-6 max-w-5xl" data-testid="billing-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight flex items-center gap-2">
            <Receipt className="h-8 w-8 text-primary" />
            Facturation & paiements
          </h1>
          <p className="text-muted-foreground mt-1">Historique des transactions, factures imprimables, gestion de l'abonnement.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchHistory} data-testid="refresh-billing">
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
          Actualiser
        </Button>
      </div>

      {/* Status card */}
      <Card data-testid="subscription-status-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center justify-between">
            <span>Abonnement</span>
            <Badge className={`border ${statusInfo.cls}`} variant="outline">{statusInfo.label}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="p-3 rounded-md bg-muted/40">
              <p className="text-xs uppercase text-muted-foreground">Plan</p>
              <p className="text-lg font-bold capitalize">
                {(() => {
                  const p = data?.organization?.plan || activeOrg.plan;
                  return p === "unlimited" ? "Gratuit illimité" : p;
                })()}
              </p>
            </div>
            <div className="p-3 rounded-md bg-muted/40">
              <p className="text-xs uppercase text-muted-foreground">Utilisateurs</p>
              <p className="text-lg font-bold">
                {(data?.organization?.plan || activeOrg.plan) === "unlimited"
                  ? "∞"
                  : (data?.organization?.slots_purchased ?? activeOrg.slots_purchased)}
              </p>
            </div>
            <div className="p-3 rounded-md bg-muted/40">
              <p className="text-xs uppercase text-muted-foreground">Total payé</p>
              <p className="text-lg font-bold">{formatCurrency(data?.total_paid_eur || 0)}</p>
            </div>
            <div className="p-3 rounded-md bg-muted/40">
              <p className="text-xs uppercase text-muted-foreground">Email facturation</p>
              <p className="text-sm font-medium truncate">{data?.organization?.billing_email || "—"}</p>
            </div>
          </div>

          {isOrgAdmin && (data?.organization?.plan || activeOrg.plan) !== "unlimited" && (
            <div className="mt-4 flex gap-2 flex-wrap">
              {subStatus === "canceled" ? (
                <Button onClick={handleResume} variant="default" size="sm" data-testid="resume-subscription-btn">
                  Réactiver l'abonnement
                </Button>
              ) : subStatus !== "trialing" && (
                <Button onClick={() => setCancelOpen(true)} variant="outline" size="sm" className="text-red-600 hover:bg-red-50 border-red-200" data-testid="cancel-subscription-btn">
                  Annuler l'abonnement
                </Button>
              )}
            </div>
          )}

          {subStatus === "past_due" && (
            <div className="mt-4 p-3 rounded-md bg-orange-50 border border-orange-200 text-sm text-orange-900 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>Paiement en retard. Régularisez votre situation pour conserver l'accès complet à l'application.</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card data-testid="transactions-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Historique des transactions ({data?.transactions?.length || 0})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 text-center text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
            </div>
          ) : (data?.transactions?.length || 0) === 0 ? (
            <p className="text-sm text-muted-foreground italic text-center py-8">
              Aucune transaction pour le moment.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-2">Date</th>
                    <th className="py-2 pr-2">Description</th>
                    <th className="py-2 pr-2">Statut</th>
                    <th className="py-2 pr-2 text-right">Montant</th>
                    <th className="py-2 pr-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {data.transactions.map((tx) => {
                    const sb = STATUS_BADGE[tx.payment_status] || { label: tx.payment_status, icon: Clock, cls: "bg-gray-100" };
                    const SBIcon = sb.icon;
                    return (
                      <tr key={tx.id} className="border-b hover:bg-muted/30" data-testid={`tx-row-${tx.id}`}>
                        <td className="py-2 pr-2 text-xs text-muted-foreground whitespace-nowrap">
                          {formatDate(tx.created_at)}
                        </td>
                        <td className="py-2 pr-2">
                          <div className="font-medium">
                            {tx.purpose === "buy_slots" ? `Achat de ${tx.slots} utilisateur(s)` : tx.purpose}
                          </div>
                          <div className="text-xs text-muted-foreground">{tx.user_email}</div>
                        </td>
                        <td className="py-2 pr-2">
                          <Badge className={`border ${sb.cls} inline-flex items-center gap-1`} variant="outline">
                            <SBIcon className="h-3 w-3" />
                            {sb.label}
                          </Badge>
                        </td>
                        <td className="py-2 pr-2 text-right font-medium">{formatCurrency(tx.amount_eur)}</td>
                        <td className="py-2 pr-2 text-right">
                          {tx.applied ? (
                            <Button
                              variant="ghost" size="sm"
                              onClick={() => handlePrintInvoice(tx)}
                              data-testid={`download-invoice-${tx.id}`}
                              title="Imprimer / enregistrer en PDF"
                            >
                              <Printer className="h-4 w-4 mr-1" /> Facture
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground italic">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel dialog */}
      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="cancel-subscription-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Annuler votre abonnement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Vous conserverez l'accès à votre espace jusqu'à la fin de la période courante. Aucune facturation ne sera engagée par la suite.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Raison (optionnel, nous aide à nous améliorer)</Label>
            <Input
              id="cancel-reason"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Trop cher, fonctionnalité manquante, autre solution…"
              data-testid="cancel-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Garder l'abonnement</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancel}
              disabled={submitting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-cancel-subscription"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              Confirmer l'annulation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
